import { useState } from 'react';
import { Typography } from 'antd';
import {
  DeleteOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  StopOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import type { ScheduleException, DayOfWeek } from '../../../types/provider';

const { Text } = Typography;

const fmtTime = (t: string) => t.slice(0, 5);

interface Props {
  item: ScheduleException;
  dayLabel: string;
  onDelete: () => void;
}

export default function OffDateRow({ item, dayLabel, onDelete }: Props) {
  const [isPressed, setIsPressed] = useState(false);

  const accent = item.fullDay ? '#ef4444' : '#fa8c16';
  const accentDark = item.fullDay ? '#dc2626' : '#d46b08';
  const dateLabel = dayjs(item.exceptionDate).format('D MMMM');

  return (
    <div
      style={{
        position: 'relative',
        borderRadius: 14,
        background: 'var(--bl-surface, #fff)',
        border: '1px solid var(--bl-divider, #ebebeb)',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'stretch',
        transition: 'transform 0.1s ease, box-shadow 0.15s ease',
        transform: isPressed ? 'scale(0.99)' : 'scale(1)',
      }}
      onTouchStart={() => setIsPressed(true)}
      onTouchEnd={() => setIsPressed(false)}
      onTouchCancel={() => setIsPressed(false)}
    >
      {/* Status gradient bar */}
      <div
        style={{
          width: 3,
          background: `linear-gradient(180deg, ${accent} 0%, ${accentDark} 100%)`,
          flexShrink: 0,
        }}
      />

      {/* Icon disc */}
      <div
        style={{
          padding: '12px 0 12px 12px',
          display: 'flex',
          alignItems: 'center',
          flexShrink: 0,
        }}
      >
        <div
          style={{
            width: 38,
            height: 38,
            borderRadius: 11,
            background: `linear-gradient(135deg, ${accent}14 0%, ${accent}26 100%)`,
            border: `1px solid ${accent}1a`,
            color: accent,
            fontSize: 15,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {item.fullDay ? <StopOutlined /> : <CalendarOutlined />}
        </div>
      </div>

      {/* Content */}
      <div
        style={{
          flex: 1,
          padding: '10px 4px 10px 12px',
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          minWidth: 0,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }}>
          <Text
            style={{
              fontSize: 14,
              fontWeight: 700,
              color: 'var(--bl-text-strong, #0f172a)',
              letterSpacing: -0.1,
            }}
          >
            {dateLabel}
          </Text>
          <Text
            style={{
              fontSize: 11,
              color: 'var(--bl-text-muted, #9ca3af)',
              fontWeight: 500,
            }}
          >
            · {dayLabel}
          </Text>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          {!item.fullDay && item.startTime && item.endTime && (
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                fontSize: 11,
                fontWeight: 600,
                color: accent,
                background: `${accent}14`,
                border: `1px solid ${accent}24`,
                borderRadius: 6,
                padding: '2px 7px',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              <ClockCircleOutlined style={{ fontSize: 9 }} />
              {fmtTime(item.startTime)}–{fmtTime(item.endTime)}
            </div>
          )}
          {item.reason && (
            <Text
              style={{
                fontSize: 12,
                color: 'var(--bl-text-muted, #6b7280)',
                fontStyle: 'italic',
                lineHeight: 1.3,
              }}
            >
              {item.reason}
            </Text>
          )}
        </div>
      </div>

      {/* Delete button */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
        aria-label="Delete"
        style={{
          width: 44,
          alignSelf: 'stretch',
          background: 'transparent',
          border: 'none',
          color: '#9ca3af',
          fontSize: 15,
          cursor: 'pointer',
          WebkitTapHighlightColor: 'transparent',
          outline: 'none',
          flexShrink: 0,
          transition: 'color 0.15s ease',
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLButtonElement).style.color = '#ef4444';
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLButtonElement).style.color = '#9ca3af';
        }}
      >
        <DeleteOutlined />
      </button>
    </div>
  );
}

// Re-export the DayOfWeek type usage for parent
export type { DayOfWeek };
